import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { History, Loader2 } from 'lucide-react';
import { adminAuditLogOptions } from '../lib/resources/queries';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';

export const Route = createFileRoute('/admin/audit-log')({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(adminAuditLogOptions(0)),
  component: AdminAuditLogPage,
});

function AdminAuditLogPage() {
  const [page, setPage] = useState(0);
  const q = useQuery(adminAuditLogOptions(page));

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-4">
      <h1 className="text-[28px] font-bold tracking-tight">Audit log</h1>
      <Card>
        <CardHeader><CardTitle>Most recent events</CardTitle></CardHeader>
        <CardContent>
          {q.isLoading ? (
            <div className="flex items-center gap-2 text-foreground-muted text-[13px]">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
          ) : !q.data || q.data.items.length === 0 ? (
            <EmptyState icon={History} title="No audit events yet"
              description="Activity (user creates, finalizes, fee changes) appears here as it happens." />
          ) : (
            <table className="w-full text-[12px]">
              <thead className="text-[11px] uppercase tracking-wide text-foreground-muted">
                <tr>
                  <th className="text-left px-2 py-2 font-medium w-44">When</th>
                  <th className="text-left px-2 py-2 font-medium w-12">By</th>
                  <th className="text-left px-2 py-2 font-medium">Action</th>
                  <th className="text-left px-2 py-2 font-medium">Entity</th>
                  <th className="text-left px-2 py-2 font-medium">Payload</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((e) => (
                  <tr key={e.id} className="border-t border-border">
                    <td className="px-2 py-1.5 font-mono">{new Date(e.ts).toISOString().slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5">{e.actorUserId ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono">{e.action}</td>
                    <td className="px-2 py-1.5">{e.entityType}#{e.entityId ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-foreground-muted truncate max-w-md">
                      {e.payload ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
        {q.data && (
          <div className="px-6 py-3 border-t border-border flex items-center gap-2 text-[12px]">
            <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Button>
            <span className="text-foreground-muted">Page {page + 1}</span>
            <Button size="sm" variant="secondary" disabled={!q.data.hasMore} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
